// UltraFlex Member Arrears Portal
// Self-service page for suspended members to settle arrears, provide new
// DD bank details, and be reinstated automatically after Stripe confirms
// the card payment.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, CreditCard, ShieldCheck, ChevronRight, ArrowLeft,
  Info, PoundSterling, Loader2, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberArrears() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    account_holder_name: user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}` : '',
    sort_code: '',
    account_number: '',
    bank_name: '',
    confirm: false,
  });

  const fetchStatus = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/dd/me/arrears-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Could not load status');
      }
      setStatus(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const validate = () => {
    if (!form.account_holder_name.trim()) return 'Account holder name required';
    const sc = form.sort_code.replace(/[-\s]/g, '');
    if (!/^\d{6}$/.test(sc)) return 'Sort code must be 6 digits (e.g. 12-34-56)';
    if (!/^\d{6,10}$/.test(form.account_number.trim())) return 'Account number must be 6-10 digits';
    if (!form.confirm) return 'Please tick the confirmation box';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/dd/me/arrears/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_holder_name: form.account_holder_name.trim(),
          sort_code: form.sort_code.trim(),
          account_number: form.account_number.trim(),
          bank_name: form.bank_name.trim() || null,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      // Redirect to Stripe
      window.location.href = data.checkout_url;
    } catch (e) { setError(e.message); setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-uf-text-muted">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" /> Loading your account status…
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-6 text-red-300">
          <XCircle className="w-6 h-6 mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (status && !status.has_arrears) {
    return (
      <div className="max-w-3xl mx-auto p-8" data-testid="no-arrears-panel">
        <button onClick={() => navigate('/hub')} className="text-uf-text-muted hover:text-white text-sm mb-4 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to hub
        </button>
        <div className="bg-emerald-900/20 border border-emerald-800 rounded-xl p-8 text-center">
          <ShieldCheck className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white mb-2">Your account is up to date</h1>
          <p className="text-uf-text-muted mb-4">You have no outstanding arrears. Enjoy your workout!</p>
          <Link to="/hub" data-testid="return-hub-link"
            className="inline-flex items-center gap-2 px-4 py-2 bg-uf-red text-white rounded-lg font-medium hover:bg-uf-red-hover">
            Return to Member Hub <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const totalOwed = status?.total_owed ?? 0;
  const principal = status?.arrears?.reduce((s, a) => s + (a.principal_amount || 0), 0) ?? 0;
  const penalty = status?.arrears?.reduce((s, a) => s + (a.penalty_amount || 0), 0) ?? 0;

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6" data-testid="arrears-portal">
      <button onClick={() => navigate('/hub')} className="text-uf-text-muted hover:text-white text-sm flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to hub
      </button>

      <div className="bg-amber-900/20 border border-amber-800 rounded-xl p-4 md:p-5 flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-1">Your membership is suspended</h1>
          <p className="text-sm text-amber-200/80">
            Your recent Direct Debit was unsuccessful. To reactivate access, please pay the outstanding
            balance and set up a new Direct Debit below. Your card will be charged today; monthly payments
            will resume via the new DD from your first collection date.
          </p>
        </div>
      </div>

      {/* Amount breakdown */}
      <div className="bg-uf-surface border border-uf-border rounded-xl p-5" data-testid="arrears-breakdown">
        <div className="flex items-center gap-2 mb-4">
          <PoundSterling className="w-5 h-5 text-uf-red" />
          <h2 className="text-lg font-semibold text-white">Amount due today</h2>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-1 border-b border-uf-border">
            <span className="text-uf-text-muted">Missed subscription</span>
            <span className="text-white font-medium">£{principal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-1 border-b border-uf-border">
            <span className="text-uf-text-muted">Failed-payment fee</span>
            <span className="text-amber-400 font-medium">£{penalty.toFixed(2)}</span>
          </div>
          <div className="flex justify-between py-2 mt-2 border-t-2 border-uf-border">
            <span className="text-white font-semibold">Total due today</span>
            <span className="text-uf-red text-lg font-bold" data-testid="total-due">£{totalOwed.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* New DD form */}
      <div className="bg-uf-surface border border-uf-border rounded-xl p-5" data-testid="new-dd-form">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-uf-red" />
          <h2 className="text-lg font-semibold text-white">Set up new Direct Debit</h2>
        </div>
        <p className="text-xs text-uf-text-muted mb-4 flex items-start gap-2">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>Your monthly subscription resumes via this new DD once the arrears are cleared. Please use
            details from the account you&apos;d like collections to come from.</span>
        </p>

        <div className="grid gap-3">
          <div>
            <label className="text-xs text-uf-text-muted uppercase tracking-wider">Account holder name</label>
            <input value={form.account_holder_name} onChange={e => setForm({...form, account_holder_name: e.target.value})}
              data-testid="input-account-holder" placeholder="J. Smith"
              className="w-full mt-1 bg-[#0f0f0f] border border-uf-border text-white rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-uf-text-muted uppercase tracking-wider">Sort code</label>
              <input value={form.sort_code} onChange={e => setForm({...form, sort_code: e.target.value})}
                data-testid="input-sort-code" placeholder="12-34-56" maxLength={8}
                className="w-full mt-1 bg-[#0f0f0f] border border-uf-border text-white rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-uf-text-muted uppercase tracking-wider">Account number</label>
              <input value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value.replace(/\D/g, '')})}
                data-testid="input-account-number" placeholder="12345678" maxLength={10}
                className="w-full mt-1 bg-[#0f0f0f] border border-uf-border text-white rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs text-uf-text-muted uppercase tracking-wider">Bank name (optional)</label>
            <input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})}
              data-testid="input-bank-name" placeholder="e.g. Barclays"
              className="w-full mt-1 bg-[#0f0f0f] border border-uf-border text-white rounded-lg px-3 py-2 text-sm" />
          </div>

          <label className="flex items-start gap-2 mt-2 cursor-pointer text-sm" data-testid="confirm-checkbox-label">
            <input type="checkbox" checked={form.confirm} onChange={e => setForm({...form, confirm: e.target.checked})}
              data-testid="confirm-checkbox" className="mt-1" />
            <span className="text-uf-text-muted">
              I confirm I am the account holder and authorise UltraFlex Gym to instruct my bank to
              collect payments from this account via Direct Debit under the Direct Debit Guarantee.
            </span>
          </label>
        </div>

        {error && (
          <div className="mt-3 text-sm text-red-400 flex items-center gap-2" data-testid="form-error">
            <XCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="pay-arrears-btn"
          className="w-full mt-5 py-3 bg-uf-red hover:bg-uf-red-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg flex items-center justify-center gap-2"
        >
          {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to secure payment…</>)
                     : (<>Pay £{totalOwed.toFixed(2)} & reinstate access <ChevronRight className="w-4 h-4" /></>)}
        </button>

        <p className="text-xs text-uf-text-muted mt-3 text-center">
          Powered by Stripe. Your card details never touch our servers.
        </p>
      </div>

      {status?.recent_events?.length > 0 && (
        <details className="bg-uf-surface border border-uf-border rounded-xl p-4">
          <summary className="cursor-pointer text-sm text-uf-text-muted">What happened? (event history)</summary>
          <ul className="mt-3 space-y-1 text-xs text-uf-text-muted font-mono">
            {status.recent_events.slice(0, 8).map((e, i) => (
              <li key={i}>
                {e.at?.slice(0, 16).replace('T', ' ')} — <span className="text-white">{e.type}</span>
                {e.reason_code && <span className="text-amber-400"> [{e.reason_code}]</span>}
                {e.amount != null && <span className="text-uf-red"> £{Number(e.amount).toFixed(2)}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
